import type { ReservationManualCreateInput } from './reservationTypes'

/**
 * 전화·현장 예약 등록 모달 폼 상태·클라이언트 검증·전화번호 자동 포맷·변환(설계 §2, #675 §2-2).
 * `reservationRescheduleViewModel.ts`와 같은 명명 관례를 따른다.
 *
 * 🚨 격자 정렬·예약 단위 배수·최대 연속 예약시간·중복·휴무 등 서버 전용 규칙은 여기서 재검증하지 않는다 —
 * 최종 판정처는 서버이며(#675 §2-2 "400과 409를 가르는 기준"), 여기 검증은 왕복을 줄이기 위한 최소한의
 * 사전 검증(필수값·연락처 형식·시간 역전·길이)만 담당한다.
 */

export interface ManualCreateFormState {
  bookerName: string
  /** 타이핑 중에도 항상 화면 표시 형식("010-0000-0000")을 유지한다 — `formatPhoneInput`이 매 입력마다 재포맷한다. */
  bookerPhone: string
  /** 코트 미선택 상태는 0으로 표현한다(`<Select>`가 항상 옵션을 갖도록 프리필하지만 코트 목록이 비어 있을 수 있다). */
  courtId: number
  /** "yyyy-MM-dd" */
  useDate: string
  /** "HH:mm" */
  startTime: string
  /** "HH:mm" — "24:00" 허용 */
  endTime: string
  paymentStatus: 'ONSITE_PENDING' | 'PAID'
  memo: string
}

/** Figma `13989-43443` 결제 상태 세그먼트 2값 — forward-only 카탈로그(`RESERVATION_STATUS_OPTIONS`와 동일 관례). */
export const MANUAL_PAYMENT_STATUS_OPTIONS: { value: ManualCreateFormState['paymentStatus']; label: string }[] = [
  { value: 'ONSITE_PENDING', label: '현장 결제 예정' },
  { value: 'PAID', label: '결제 완료' },
]

/** 캘린더에서 선택 중이던 날짜로 프리필한다 — 이름·연락처·시간·메모는 매번 새로 입력받아야 하므로 빈 값. */
export function initialManualCreateFormState(defaults: { useDate: string; courtId?: number }): ManualCreateFormState {
  return {
    bookerName: '',
    bookerPhone: '',
    courtId: defaults.courtId ?? 0,
    useDate: defaults.useDate,
    startTime: '',
    endTime: '',
    paymentStatus: 'ONSITE_PENDING',
    memo: '',
  }
}

const PHONE_DIGITS_MAX = 11

/**
 * 숫자만 추출해 010 휴대전화 형식(3-4-4)으로 타이핑 중 자동 하이픈을 삽입한다.
 * 🚨 010 이외의 지역번호(예: 02-000-0000)는 다루지 않는다 — 이슈 문구가 "010-0000-0000 자동 포맷"으로
 * 명시했고 Figma placeholder도 010 하나뿐이다(설계 D5, YAGNI).
 */
export function formatPhoneInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, PHONE_DIGITS_MAX)
  if (digits.length <= 3) return digits
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`
}

export interface ManualCreateFormErrors {
  bookerName?: string
  bookerPhone?: string
  courtId?: string
  useDate?: string
  time?: string
  memo?: string
}

export function hasManualCreateFormErrors(errors: ManualCreateFormErrors): boolean {
  return Object.keys(errors).length > 0
}

const MAX_NAME_LENGTH = 50
const MAX_MEMO_LENGTH = 500
const PHONE_PATTERN = /^010-\d{4}-\d{4}$/

/** "HH:mm" → 자정으로부터의 분. "24:00" 은 1440(`reservationRescheduleViewModel.ts` 규칙 승계). */
function toMinutes(time: string): number {
  const [hoursText, minutesText] = time.split(':')
  return Number(hoursText ?? 0) * 60 + Number(minutesText ?? 0)
}

export function validateManualCreateForm(form: ManualCreateFormState): ManualCreateFormErrors {
  const errors: ManualCreateFormErrors = {}

  const trimmedName = form.bookerName.trim()
  if (trimmedName.length === 0) {
    errors.bookerName = '예약자 이름을 입력해 주세요.'
  } else if (trimmedName.length > MAX_NAME_LENGTH) {
    errors.bookerName = `이름은 최대 ${MAX_NAME_LENGTH}자까지 입력할 수 있습니다.`
  }

  if (!PHONE_PATTERN.test(form.bookerPhone)) {
    errors.bookerPhone = '연락처를 010-0000-0000 형식으로 입력해 주세요.'
  }

  if (!form.courtId) errors.courtId = '코트를 선택해 주세요.'
  if (form.useDate.length === 0) errors.useDate = '날짜를 선택해 주세요.'

  if (form.startTime.length === 0 || form.endTime.length === 0) {
    errors.time = '시작·종료 시각을 선택해 주세요.'
  } else if (toMinutes(form.endTime) <= toMinutes(form.startTime)) {
    errors.time = '종료 시각은 시작 시각보다 뒤여야 합니다.'
  }

  if (form.memo.trim().length > MAX_MEMO_LENGTH) {
    errors.memo = `메모는 최대 ${MAX_MEMO_LENGTH}자까지 입력할 수 있습니다.`
  }

  return errors
}

/** 등록 요청 바디로 변환 — 이름 trim, 빈 메모는 `undefined`(서버 `memo: String?`와 `""`의 의미 차이를 만들지 않는다). */
export function toManualCreateInput(form: ManualCreateFormState): ReservationManualCreateInput {
  const trimmedMemo = form.memo.trim()
  return {
    bookerName: form.bookerName.trim(),
    bookerPhone: form.bookerPhone,
    courtId: form.courtId,
    useDate: form.useDate,
    startTime: form.startTime,
    endTime: form.endTime,
    paymentStatus: form.paymentStatus,
    memo: trimmedMemo.length > 0 ? trimmedMemo : undefined,
  }
}
